// Game detail (UC-05): court, time, roster, and join/leave (UC-06/07).
// For the organizer, entry points to manage (UC-14) and find players (UC-10).

import $ from 'jquery';
import type { GameDetail } from '@shared/dto';
import type { Screen } from './types';
import { api } from '../api';
import { refresh } from '../router';
import { avatarHtml, esc } from '../ui/dom';
import { FORMAT_TR, SKILL_TR, skillClass, STATUS_TR, whenLong } from '../ui/format';
import { loadingHtml } from '../ui/states';
import { toast } from '../ui/toast';
import { refreshUnread } from '../session';

const RELATION_BADGE: Record<string, string> = {
  organizer: '<span class="badge badge--accent">Organizatör</span>',
  self: '<span class="badge">Sen</span>',
  friend: '<span class="badge badge--friend">👥 Arkadaş</span>',
  stranger: '',
};

function rosterHtml(d: GameDetail): string {
  const rows = d.roster
    .map((m) => {
      // A stranger on a public game: identity hidden, shown as a compact locked row.
      if (m.masked) {
        return `<div class="roster__row roster__row--masked">
          <span class="avatar avatar--masked" style="width:36px;height:36px">🔒</span>
          <span class="roster__name">Gizli oyuncu</span>
        </div>`;
      }
      const p = m.user;
      return `<div class="roster__row">
        ${avatarHtml(p.name, p.avatarUrl, 36)}
        <span class="roster__name">${esc(p.name)}</span>
        <span class="roster__area">${esc(p.homeArea)}</span>
        ${RELATION_BADGE[m.relation] ?? ''}
      </div>`;
    })
    .join('');
  const emptySlots = Math.max(0, d.game.capacity - d.roster.length);
  const slots = Array.from({ length: emptySlots })
    .map(() => `<div class="roster__row roster__row--empty">Boş slot</div>`)
    .join('');
  return rows + slots;
}

function requestsHtml(d: GameDetail): string {
  if (!d.viewerIsOrganizer || d.joinRequests.length === 0) return '';
  const rows = d.joinRequests
    .map(
      (r) => `<div class="roster__row">
        ${avatarHtml(r.player.name, r.player.avatarUrl, 36)}
        <span class="roster__name">${esc(r.player.name)}</span>
        <span class="roster__area">${esc(r.player.homeArea)}</span>
        <button class="btn btn--primary btn--sm" data-approve="${esc(r.requestId)}">Kabul</button>
        <button class="btn btn--danger btn--sm" data-decline="${esc(r.requestId)}">Reddet</button>
      </div>`,
    )
    .join('');
  return `
    <div class="section-title">Katılma istekleri (${d.joinRequests.length})</div>
    <div class="roster roster--requests">${rows}</div>`;
}

function actionsHtml(d: GameDetail): string {
  if (d.game.status === 'cancelled') {
    return `<div class="detail__cancelled">Bu oyun iptal edildi.</div>`;
  }
  if (d.viewerIsOrganizer) {
    const missing = d.spotsLeft > 0;
    return `
      ${missing ? `<button class="btn btn--primary btn--block" id="act-find">🔍 Eksik oyuncu bul (${d.spotsLeft})</button>` : ''}
      <button class="btn btn--ghost btn--block" id="act-manage">Oyunu yönet</button>`;
  }
  if (d.viewerIsParticipant) {
    return `<button class="btn btn--danger btn--block" id="act-leave">Oyundan ayrıl</button>`;
  }
  // Invited players skip the queue — they were offered the spot, so they join directly.
  if (d.viewerHasPendingInvite) {
    return `<button class="btn btn--primary btn--block" id="act-join">✅ Daveti kabul et ve katıl</button>`;
  }
  if (d.viewerHasPendingRequest) {
    return `<button class="btn btn--ghost btn--block" disabled>⏳ Katılma isteğin gönderildi</button>`;
  }
  if (d.spotsLeft === 0) {
    return `<button class="btn btn--ghost btn--block" disabled>Oyun dolu</button>`;
  }
  // Discovered from the list without an invite → may only ask to join.
  return `<button class="btn btn--primary btn--block" id="act-request">Katılma isteği gönder</button>`;
}

export const GameDetailScreen: Screen = async (body, params) => {
  body.html(loadingHtml());
  const d = await api.call('games.get', { gameId: params.id });

  body.html(`
    <div class="detail">
      <div class="detail__hero">
        <img class="detail__map" src="${esc(d.court.mapImageUrl)}" alt="" onerror="this.style.display='none'" />
        <div class="detail__court">${esc(d.court.name)}</div>
        <div class="detail__area">${esc(d.court.area)} · ${d.distanceKm} km · ${d.court.indoor ? 'Kapalı' : 'Açık'}${d.court.isFree ? ' · Ücretsiz' : ''}</div>
      </div>

      <div class="detail__when">📅 ${esc(whenLong(d.game.startsAt))}</div>

      <div class="detail__meta">
        <span class="badge">${FORMAT_TR[d.game.format]}</span>
        <span class="badge ${skillClass(d.game.skill)}">${SKILL_TR[d.game.skill]}</span>
        <span class="badge">${d.game.visibility === 'private' ? '🔒 Özel' : '🌐 Herkese açık'}</span>
        <span class="badge">${d.game.status === 'full' ? STATUS_TR.full : `${d.game.participantIds.length}/${d.game.capacity}`}</span>
      </div>

      ${requestsHtml(d)}

      <div class="section-title">Oyuncular (${d.roster.length}/${d.game.capacity})</div>
      <div class="roster">${rosterHtml(d)}</div>

      <div class="detail__actions">${actionsHtml(d)}</div>
    </div>
  `);

  $('#act-join').on('click', async () => {
    try {
      await api.call('games.join', { gameId: d.game.id });
      toast('Oyuna katıldın 🎉', 'success');
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  $('#act-leave').on('click', async () => {
    await api.call('games.leave', { gameId: d.game.id });
    toast('Oyundan ayrıldın', 'info');
    refresh();
  });

  $('#act-request').on('click', async () => {
    try {
      await api.call('games.requestJoin', { gameId: d.game.id });
      toast('Katılma isteğin gönderildi ⏳', 'success');
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  body.on('click', '[data-approve]', async function () {
    const requestId = $(this).data('approve') as string;
    try {
      await api.call('games.respondRequest', { requestId, accept: true });
      toast('Oyuncu kadroya eklendi 🎉', 'success');
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  body.on('click', '[data-decline]', async function () {
    const requestId = $(this).data('decline') as string;
    await api.call('games.respondRequest', { requestId, accept: false });
    toast('İstek reddedildi', 'info');
    refresh();
  });

  $('#act-find').on('click', () => {
    location.hash = `#/find/${d.game.id}`;
  });
  $('#act-manage').on('click', () => {
    location.hash = `#/manage/${d.game.id}`;
  });

  void refreshUnread();
};
